import { useEffect, useState } from 'react';
import { Loader2, ArrowUpRight, CreditCard, FileText, FileCheck, FileDown, TrendingUp, Users, Calendar, ChevronRight, DollarSign, LineChart, Building, ReceiptText, Phone, Briefcase, Mail, PlusCircle, Package, Warehouse, Factory, ShoppingCart, Truck, AlertTriangle, BarChart3, ClipboardList } from 'lucide-react';
import type { Analytics, Currency } from '@/types';
import CountsService from '@/api/counts.service';
import InvoiceService from '@/api/invoices.service';
import ReceiptService from '@/api/receipts.service';
import QuotationService from '@/api/quotations.service';
import CrmActivitiesService from '@/api/crmActivities.service';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';
import { useToast } from "@/hooks/use-toast";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, StripeCard, StripeStatCard } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { canView, canCreate, validatePermissions } from '@/lib/permissions';
import { useBrand } from '@/lib/brand';

// Define view types
type ViewType = 'dashboard' | 'accounting' | 'sales' | 'crm' | 'inventory' | 'manufacturing' | 'procurement';

// Define view configurations
const viewConfigs = {
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Overview of your business',
    actionText: 'Create New Invoice',
    actionUrl: '/invoices/create',
    icon: <LineChart className="h-5 w-5" />,
    color: 'text-slate-800',
    bgColor: 'from-slate-50 to-slate-100',
    buttonVariant: 'default',
  },
  accounting: {
    title: 'Accounting & Finance',
    subtitle: 'Track your financial performance',
    actionText: 'Create New Invoice',
    actionUrl: '/invoices/create',
    icon: <DollarSign className="h-5 w-5" />,
    color: 'text-blue-800',
    bgColor: 'from-blue-50 to-blue-100',
    buttonVariant: 'stripe',
  },
  sales: {
    title: 'Sales Management',
    subtitle: 'Track deals and sales performance',
    actionText: 'Create New Deal',
    actionUrl: '/deals/create',
    icon: <LineChart className="h-5 w-5" />,
    color: 'text-green-800',
    bgColor: 'from-green-50 to-green-100',
    buttonVariant: 'default',
  },
  crm: {
    title: 'Customer Relationship',
    subtitle: 'Manage your customer relationships',
    actionText: 'Add New Customer',
    actionUrl: '/customers/create',
    icon: <Users className="h-5 w-5" />,
    color: 'text-purple-800',
    bgColor: 'from-purple-50 to-purple-100',
    buttonVariant: 'default',
  },
  inventory: {
    title: 'Inventory Management',
    subtitle: 'Track and manage your inventory',
    actionText: 'Add New Item',
    actionUrl: '/inventory/items/create',
    icon: <Package className="h-5 w-5" />,
    color: 'text-orange-800',
    bgColor: 'from-orange-50 to-orange-100',
    buttonVariant: 'default',
  },
  manufacturing: {
    title: 'Manufacturing Operations',
    subtitle: 'Manage production and manufacturing',
    actionText: 'Create Production Job',
    actionUrl: '/manufacturing/production-jobs/create',
    icon: <Building className="h-5 w-5" />,
    color: 'text-indigo-800',
    bgColor: 'from-indigo-50 to-indigo-100',
    buttonVariant: 'default',
  },
  procurement: {
    title: 'Procurement Management',
    subtitle: 'Manage purchasing and suppliers',
    actionText: 'Create Purchase Order',
    actionUrl: '/procurement/purchase-orders/create',
    icon: <Briefcase className="h-5 w-5" />,
    color: 'text-teal-800',
    bgColor: 'from-teal-50 to-teal-100',
    buttonVariant: 'default',
  }
};

export default function Dashboard() {
  const brand = useBrand();
  const [currency] = useState<Currency>('USD');
  const [analytics, setAnalytics] = useState<Partial<Analytics>>({});
  const [receiptsData, setReceiptsData] = useState<any[]>([]);
  const [loadingStates, setLoadingStates] = useState({
    counts: true,
    invoices: true,
    receipts: true,
    quotations: true,
    monthlyReceipts: true,
    crmActivities: true
  });
  const [quotationConversionRate, setQuotationConversionRate] = useState<number>(0);
  const [selectedTimeframe, setSelectedTimeframe] = useState('monthly');
  const [crmActivities, setCrmActivities] = useState<any[]>([]);

  const { toast } = useToast();

  const currencySymbols: Record<Currency, string> = {
    USD: '$',
    ZWG: 'ZWG',
    ZAR: 'R',
    GBP: '£',
    EUR: '€',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'CHF',
    CNY: '¥',
    HKD: 'HK$',
    INR: '₹',
    JPY: '¥',
    MXN: 'MX$',
    NZD: 'NZ$',
    RUB: '₽',
    SAR: 'SR',
    SGD: 'S$',
    THB: '฿',
    TRY: '₺',
    UAH: '₴',
    VND: '₫',
    ZMW: 'ZK',
    MZN: 'MT',
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  // Get current view
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const storedView = typeof window !== 'undefined' ? window.localStorage.getItem('currentView') : null;
    return (storedView || 'dashboard') as ViewType;
  });

  // Get the view config for the current view
  const viewConfig = viewConfigs[currentView] || viewConfigs.dashboard;

  // Validate permissions on component mount
  useEffect(() => {
    validatePermissions();
  }, []);

  useEffect(() => {
    if (!selectedBusiness) return;

    // Fetch counts one by one
    const fetchCounts = async () => {
      try {
        const invoicesCount = await CountsService.getAllInvoicesCount(selectedBusiness.id);
        setAnalytics(prev => ({
          ...prev,
          totalInvoices: invoicesCount.data.result === "success" ? invoicesCount.data.count : 0
        }));

        const receiptsCount = await CountsService.getAllReceiptsCount(selectedBusiness.id);
        setAnalytics(prev => ({
          ...prev,
          totalReceipts: receiptsCount.data.result === "success" ? receiptsCount.data.count : 0
        }));

        const quotationsCount = await CountsService.getAllQuotationsCount(selectedBusiness.id);
        setAnalytics(prev => ({
          ...prev,
          totalQuotations: quotationsCount.data.result === "success" ? quotationsCount.data.count : 0
        }));
      } finally {
        setLoadingStates(prev => ({ ...prev, counts: false }));
      }
    };
    
    // Fetch pending invoices amount
    const fetchPendingInvoicesAmount = async () => {
      try {
        const response = await InvoiceService.getInvoicesByPendingStatus(selectedBusiness.id);
        const pendingInvoices = response.data.result === "success" ? response.data.invoices : [];
        const totalPendingAmount = pendingInvoices.reduce((sum: number, doc: any) => 
          sum + doc.amount, 0);

        setAnalytics(prev => ({
          ...prev,
          totalPendingAmount
        }));
      } finally {
        setLoadingStates(prev => ({ ...prev, invoices: false }));
      }
    };

    // Fetch invoice status counts
    const fetchInvoiceStatusCounts = async () => {
      try {
        const response = await InvoiceService.getInvoicesShowingOnlyStatus(selectedBusiness.id);
        const statusCounts = response.data.result === "success" ? response.data.counts : {};
        
        setAnalytics(prev => ({
          ...prev,
          invoicesByStatus: {
            pending: statusCounts.pending || 0,
            paid: statusCounts.paid || 0, 
            partially_paid: statusCounts.partially_paid || 0
          }
        }));

        if (analytics.recentQuotations) {
          const acceptedQuotations = analytics.recentQuotations.filter(
            doc => doc.status === 'accepted'
          ).length;
          const totalQuotations = analytics.recentQuotations.length;
          setQuotationConversionRate(totalQuotations ? (acceptedQuotations / totalQuotations) : 0);
        }
    
      } finally {
        setLoadingStates(prev => ({ ...prev, invoices: false }));
      }
    };

    const fetchTotalPaidAmount = async () => {
      try {
        const response = await ReceiptService.getTotalPaidAmount(selectedBusiness.id);
        const totalPaidAmount = response.data.result === "success" ? response.data.totalPaidAmount : 0;

        setAnalytics(prev => ({
          ...prev,
          totalPaidAmount
        }));
      } finally {
        setLoadingStates(prev => ({ ...prev, receipts: false }));
      }
    };

    // Fetch receipts
    const fetchRecentReceipts = async () => {
      try {
        const response = await ReceiptService.getAllRecentReceipts(selectedBusiness.id, 0, 5);
        const receipts = response.data.result === "success" ? response.data.receipts : [];
        setAnalytics(prev => ({
          ...prev,
          recentReceipts: receipts
        }));
      } finally {
        setLoadingStates(prev => ({ ...prev, receipts: false }));
      }
    };

    // Fetch quotations
    const fetchRecentQuotations = async () => {
      try {
        const response = await QuotationService.getAllRecentQuotations(selectedBusiness.id, 0, 5);
        const quotations = response.data.result === "success" ? response.data.quotations : [];
        setAnalytics(prev => ({
          ...prev,
          recentQuotations: quotations
        }));

        const acceptedQuotations = quotations.filter(
          (doc: any) => doc.status === 'accepted'
        ).length;
        const totalQuotations = quotations.length;
        setQuotationConversionRate(totalQuotations ? (acceptedQuotations / totalQuotations) : 0);
  
      } finally {
        setLoadingStates(prev => ({ ...prev, quotations: false }));
      }
    };

    const fetchRecentInvoices = async () => {
      try {
        const response = await InvoiceService.getAllRecentInvoices(selectedBusiness.id, 0, 5);
        const invoices = response.data.result === "success" ? response.data.invoices : [];
        setAnalytics(prev => ({
          ...prev,
          recentInvoices: invoices
        }));
      } finally {
        setLoadingStates(prev => ({ ...prev, invoices: false }));
      }
    };
  
    // Fetch monthly receipts data
    const fetchMonthlyReceipts = async () => {
      try {
        const response = await ReceiptService.getAllRecentReceipts(selectedBusiness.id);
        if (response.data.result === 'success') {
          // Create a map to store data by year-month
          const monthlyDataMap = new Map();
          
          response.data.receipts.forEach((receipt: any) => {
            // Extract month and year from date string (format: dd/mm/yyyy)
            const [_, month, year] = receipt.paymentDate.split('/');
            const key = `${year}-${month}`;
            
            if (!monthlyDataMap.has(key)) {
              monthlyDataMap.set(key, 0);
            }
            monthlyDataMap.set(key, monthlyDataMap.get(key) + receipt.amount);
          });

          // Convert map to array and sort by date
          const sortedData = Array.from(monthlyDataMap.entries())
            .map(([key, amount]) => {
              const [year, month] = key.split('-');
              return {
                month: new Date(parseInt(year), parseInt(month) - 1)
                  .toLocaleString('default', { month: 'short', year: 'numeric' }),
                amount,
                formattedAmount: formatCurrency(amount as number, currency),
              };
            })
            .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

          setReceiptsData(sortedData);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch monthly receipts data",
          variant: "destructive",
        });
      } finally {
        setLoadingStates(prev => ({ ...prev, monthlyReceipts: false }));
      }
    };

    // Fetch CRM activities
    const fetchCrmActivities = async () => {
      try {
        const response = await CrmActivitiesService.getRecentActivities(selectedBusiness.id);
        if (response.data.result === "success") {
          setCrmActivities(response.data.crmActivities || []);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch CRM activities",
          variant: "destructive",
        });
      } finally {
        setLoadingStates(prev => ({ ...prev, crmActivities: false }));
      }
    };

    // Run fetches sequentially
    const fetchAllData = async () => {
      await fetchCounts();
      await fetchPendingInvoicesAmount();
      await fetchInvoiceStatusCounts();
      await fetchTotalPaidAmount();
      await fetchRecentReceipts();
      await fetchRecentQuotations();
      await fetchRecentInvoices();
      await fetchMonthlyReceipts();
      await fetchCrmActivities();
    };

    fetchAllData();
  }, [selectedBusiness]);

  // Format currency values
  const formatCurrency = (value: number, currency: Currency): string => {
    return `${currencySymbols[currency]}${value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Calculate the total invoiced amount
  const totalInvoicedAmount = 
    (analytics.totalPendingAmount || 0) + 
    (analytics.totalPaidAmount || 0);

  // Get the proper loading state or value for each stat
  const getStatValue = (key: string, formatter?: (value: any) => any) => {
    if (loadingStates.counts || loadingStates.invoices || loadingStates.receipts) {
      return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />;
    }
    
    const value = analytics[key as keyof typeof analytics];
    return formatter ? formatter(value) : value;
  };

  // MoyoTools module cards
  const moduleCards = [
    {
      title: "Accounting & Finance",
      description: "Manage invoices, track expenses, and generate financial reports",
      icon: <DollarSign className="w-10 h-10 text-blue-500" />,
      href: "/",
      active: currentView === 'accounting',
      viewId: 'accounting',
      moduleName: 'Accounting'
    },
    {
      title: "Sales Management",
      description: "Track deals, manage your pipeline, and close more business",
      icon: <LineChart className="w-10 h-10 text-green-500" />,
      href: "/",
      active: currentView === 'sales',
      viewId: 'sales',
      moduleName: 'Sales'
    },
    {
      title: "Customer Relationship",
      description: "Manage contacts, campaigns, and customer communications",
      icon: <Users className="w-10 h-10 text-purple-500" />,
      href: "/",
      active: currentView === 'crm',
      viewId: 'crm',
      moduleName: 'CRM'
    },
    {
      title: "Business Management",
      description: "Manage your business portfolio and settings",
      icon: <Building className="w-10 h-10 text-amber-500" />,
      href: "/businesses",
      active: false,
      viewId: 'dashboard',
      moduleName: 'Business'
    }
  ];
  
  // Filter module cards based on permissions
  const availableModuleCards = moduleCards.filter(card => canView(card.moduleName));

  // Stats for the dashboard
  const stats = [
    { 
      name: 'Total Invoices', 
      value: getStatValue('totalInvoices'),
      icon: <FileText className="w-5 h-5 text-blue-500" />,
      href: '/invoices',
      color: 'bg-blue-50 dark:bg-blue-950',
      views: ['dashboard', 'accounting'],
      moduleName: 'Invoices'
    },
    { 
      name: 'Total Receipts', 
      value: getStatValue('totalReceipts'),
      icon: <FileCheck className="w-5 h-5 text-green-500" />,
      href: '/receipts',
      color: 'bg-green-50 dark:bg-green-950',
      views: ['dashboard', 'accounting'],
      moduleName: 'Receipts'
    },
    { 
      name: 'Total Quotations', 
      value: getStatValue('totalQuotations'),
      icon: <FileDown className="w-5 h-5 text-purple-500" />,
      href: '/quotations',
      color: 'bg-purple-50 dark:bg-purple-950',
      views: ['dashboard', 'sales', 'accounting'],
      moduleName: 'Quotations'
    },
    { 
      name: 'Conversion Rate', 
      value: loadingStates.quotations ? <Loader2 className="w-4 h-4 animate-spin" /> : `${(quotationConversionRate * 100).toFixed(1)}%`,
      icon: <TrendingUp className="w-5 h-5 text-amber-500" />,
      href: '/quotations',
      color: 'bg-amber-50 dark:bg-amber-950',
      views: ['dashboard', 'sales'],
      moduleName: 'Quotations'
    },
  ];

  // Filter stats based on permissions and current view
  const filteredStats = stats.filter(stat => 
    stat.views.includes(currentView) && canView(stat.moduleName)
  );

  // Financial stats
  const financialStats = [
    {
      name: 'Revenue Collected',
      value: getStatValue('totalPaidAmount', (value) => formatCurrency(value || 0, currency)),
      description: 'Total amount collected from invoices',
      icon: <CreditCard className="w-5 h-5 text-green-500" />,
      trend: '+12.5% from last month',
      trendUp: true,
    },
    {
      name: 'Pending Invoices',
      value: getStatValue('totalPendingAmount', (value) => formatCurrency(value || 0, currency)),
      description: 'Amount yet to be collected',
      icon: <Calendar className="w-5 h-5 text-amber-500" />,
      trend: 'From 5 invoices',
      trendUp: false, 
    }
  ];

  // View-specific stats for the Sales view
  const salesStats = [
    { 
      name: 'Open Deals', 
      value: 12, // This would come from actual API
      icon: <Briefcase className="w-5 h-5 text-blue-500" />,
      href: '/deals',
      color: 'bg-blue-50 dark:bg-blue-950',
      moduleName: 'Deals'
    },
    { 
      name: 'Pipeline Value', 
      value: formatCurrency(52000, currency), // This would come from actual API
      icon: <LineChart className="w-5 h-5 text-green-500" />,
      href: '/sales-pipeline',
      color: 'bg-green-50 dark:bg-green-950',
      moduleName: 'Sales'
    },
    { 
      name: 'Win Rate', 
      value: '68%', // This would come from actual API
      icon: <TrendingUp className="w-5 h-5 text-amber-500" />,
      href: '/sales-reports',
      color: 'bg-amber-50 dark:bg-amber-950',
      moduleName: 'Sales'
    },
    { 
      name: 'Avg. Deal Size', 
      value: formatCurrency(4333, currency), // This would come from actual API
      icon: <DollarSign className="w-5 h-5 text-purple-500" />,
      href: '/sales-reports',
      color: 'bg-purple-50 dark:bg-purple-950',
      moduleName: 'Sales'
    },
  ];

  // Filter sales stats based on permissions
  const filteredSalesStats = salesStats.filter(stat => canView(stat.moduleName));

  // Switch to view function
  const switchToView = (viewId: ViewType) => {
    setCurrentView(viewId);
    localStorage.setItem('currentView', viewId);
    // Navigate to the main page for this view if needed
    window.location.reload();
  };

  // View-specific stats for the CRM view
  const crmStats = [
    { 
      name: 'Total Customers', 
      value: 47, // This would come from actual API
      icon: <Users className="w-5 h-5 text-purple-500" />,
      href: '/customers',
      color: 'bg-purple-50 dark:bg-purple-950',
      moduleName: 'Customers'
    },
    { 
      name: 'Active Campaigns', 
      value: 3, // This would come from actual API
      icon: <ReceiptText className="w-5 h-5 text-blue-500" />,
      href: '/campaigns',
      color: 'bg-blue-50 dark:bg-blue-950',
      moduleName: 'Campaigns'
    },
    { 
      name: 'Upcoming Meetings', 
      value: 8, // This would come from actual API
      icon: <Calendar className="w-5 h-5 text-amber-500" />,
      href: '/calendar',
      color: 'bg-amber-50 dark:bg-amber-950',
      moduleName: 'Calendar'
    },
    { 
      name: 'Recent Communications', 
      value: 24, // This would come from actual API
      icon: <Phone className="w-5 h-5 text-green-500" />,
      href: '/communications',
      color: 'bg-green-50 dark:bg-green-950',
      moduleName: 'CRM'
    },
  ];

  // Filter CRM stats based on permissions
  const filteredCrmStats = crmStats.filter(stat => canView(stat.moduleName));

  // View-specific stats for the Inventory view
  const inventoryStats = [
    { 
      name: 'Total Items', 
      value: 156, // This would come from actual API
      icon: <Package className="w-5 h-5 text-orange-500" />,
      href: '/inventory/items',
      color: 'bg-orange-50 dark:bg-orange-950',
      moduleName: 'Inventory'
    },
    { 
      name: 'Low Stock Items', 
      value: 12, // This would come from actual API
      icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
      href: '/inventory/reorder-alerts',
      color: 'bg-red-50 dark:bg-red-950',
      moduleName: 'Inventory'
    },
    { 
      name: 'Total Value', 
      value: formatCurrency(89500, currency), // This would come from actual API
      icon: <DollarSign className="w-5 h-5 text-green-500" />,
      href: '/inventory/reports',
      color: 'bg-green-50 dark:bg-green-950',
      moduleName: 'Inventory'
    },
    { 
      name: 'Warehouses', 
      value: 3, // This would come from actual API
      icon: <Warehouse className="w-5 h-5 text-blue-500" />,
      href: '/warehouses',
      color: 'bg-blue-50 dark:bg-blue-950',
      moduleName: 'Inventory'
    },
  ];

  // Filter inventory stats based on permissions
  const filteredInventoryStats = inventoryStats.filter(stat => canView(stat.moduleName));

  // View-specific stats for the Manufacturing view
  const manufacturingStats = [
    { 
      name: 'Active Production Jobs', 
      value: 8, // This would come from actual API
      icon: <Factory className="w-5 h-5 text-indigo-500" />,
      href: '/manufacturing/production-jobs',
      color: 'bg-indigo-50 dark:bg-indigo-950',
      moduleName: 'Manufacturing'
    },
    { 
      name: 'Work Orders', 
      value: 15, // This would come from actual API
      icon: <ClipboardList className="w-5 h-5 text-purple-500" />,
      href: '/manufacturing/work-orders',
      color: 'bg-purple-50 dark:bg-purple-950',
      moduleName: 'Manufacturing'
    },
    { 
      name: 'Production Efficiency', 
      value: '94%', // This would come from actual API
      icon: <TrendingUp className="w-5 h-5 text-green-500" />,
      href: '/manufacturing/reports',
      color: 'bg-green-50 dark:bg-green-950',
      moduleName: 'Manufacturing'
    },
    { 
      name: 'Recipes (BOM)', 
      value: 42, // This would come from actual API
      icon: <FileText className="w-5 h-5 text-amber-500" />,
      href: '/manufacturing/recipes',
      color: 'bg-amber-50 dark:bg-amber-950',
      moduleName: 'Manufacturing'
    },
  ];

  // Filter manufacturing stats based on permissions
  const filteredManufacturingStats = manufacturingStats.filter(stat => canView(stat.moduleName));

  // View-specific stats for the Procurement view
  const procurementStats = [
    { 
      name: 'Purchase Orders', 
      value: 23, // This would come from actual API
      icon: <ShoppingCart className="w-5 h-5 text-teal-500" />,
      href: '/procurement/purchase-orders',
      color: 'bg-teal-50 dark:bg-teal-950',
      moduleName: 'Procurement'
    },
    { 
      name: 'Pending Approvals', 
      value: 5, // This would come from actual API
      icon: <ClipboardList className="w-5 h-5 text-amber-500" />,
      href: '/procurement/pending-approvals',
      color: 'bg-amber-50 dark:bg-amber-950',
      moduleName: 'Procurement'
    },
    { 
      name: 'Total Spend', 
      value: formatCurrency(125000, currency), // This would come from actual API
      icon: <DollarSign className="w-5 h-5 text-green-500" />,
      href: '/procurement/reports',
      color: 'bg-green-50 dark:bg-green-950',
      moduleName: 'Procurement'
    },
    { 
      name: 'Suppliers', 
      value: 18, // This would come from actual API
      icon: <Building className="w-5 h-5 text-blue-500" />,
      href: '/procurement/supplier-mall',
      color: 'bg-blue-50 dark:bg-blue-950',
      moduleName: 'Procurement'
    },
  ];

  // Filter procurement stats based on permissions
  const filteredProcurementStats = procurementStats.filter(stat => canView(stat.moduleName));

  // Get icon based on activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4 text-blue-700" />;
      case 'email':
        return <Mail className="h-4 w-4 text-green-700" />;
      case 'meeting':
        return <Calendar className="h-4 w-4 text-amber-700" />;
      case 'note':
        return <FileText className="h-4 w-4 text-purple-700" />;
      case 'task':
        return <FileCheck className="h-4 w-4 text-slate-700" />;
      default:
        return <FileText className="h-4 w-4 text-primary" />;
    }
  };

  // Get color based on activity type
  const getActivityColorClass = (type: string) => {
    switch (type) {
      case 'call':
        return 'bg-blue-100';
      case 'email':
        return 'bg-green-100';
      case 'meeting':
        return 'bg-amber-100';
      case 'note':
        return 'bg-purple-100';
      case 'task':
        return 'bg-slate-100';
      default:
        return 'bg-primary/10';
    }
  };

  // Format activity date
  const formatActivityDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Check if date is today, yesterday, or tomorrow
    if (date.toDateString() === now.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      // More than a day away
      const days = Math.round((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (days < 0) {
        return `${Math.abs(days)} days ago`;
      } else {
        return `In ${days} days`;
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome section with basic business stats */}
      <div className={cn(
        "p-6 rounded-lg border bg-gradient-to-r", 
        viewConfig.bgColor
      )}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              {viewConfig.icon}
              <h1 className={cn("text-3xl font-bold tracking-tight", viewConfig.color)}>
                {viewConfig.title}
              </h1>
            </div>
            <p className="text-muted-foreground mt-1">
              {viewConfig.subtitle} {selectedBusiness ? `for ${selectedBusiness.name}` : ''}
            </p>
          </div>
          {canCreate(currentView === 'accounting' 
              ? 'Invoices' 
              : currentView === 'sales' 
                ? 'Deals' 
                : currentView === 'crm' 
                  ? 'Customers' 
                  : 'Dashboard') && (
            <Button variant={viewConfig.buttonVariant as any} size="sm" className="w-full md:w-auto" asChild>
              <a href={viewConfig.actionUrl}>{viewConfig.actionText}</a>
            </Button>
          )}
        </div>
      </div>

      {/* MoyoTools Module Cards - Only show when in dashboard view */}
      {currentView === 'dashboard' && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-2">{brand.tagline}</h2>
              <p className="text-muted-foreground">
                {brand.description}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {availableModuleCards.map((card, index) => (
              <Card 
                key={index} 
                className={cn(
                  "overflow-hidden transition-all hover:shadow-md cursor-pointer", 
                  card.active && "border-primary ring-1 ring-primary"
                )}
                onClick={() => switchToView(card.viewId as ViewType)}
              >
                <CardHeader className="pb-2">
                  <div className="mb-2">{card.icon}</div>
                  <CardTitle className="text-base">{card.title}</CardTitle>
                  <CardDescription className="text-xs">
                    {card.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      Switch to {card.title.split(' ')[0]} <ChevronRight className="w-3 h-3" />
                    </span>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Key metrics / stats cards based on current view */}
      {(currentView === 'dashboard' || currentView === 'accounting') && (
        <div>
          <h2 className="text-xl font-medium mb-4">Financial Overview</h2>
          <div className="stripe-dashboard-grid">
            {filteredStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View all <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Sales View Stats */}
      {currentView === 'sales' && (
        <div>
          <h2 className="text-xl font-medium mb-4">Sales Performance</h2>
          <div className="stripe-dashboard-grid">
            {filteredSalesStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View details <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Conversion stats from dashboard - show if relevant */}
          {filteredStats
            .filter(stat => stat.views.includes('sales'))
            .map((stat) => (
              <Card key={stat.name} className="overflow-hidden mt-6">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View all <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))
          }
        </div>
      )}

      {/* CRM View Stats */}
      {currentView === 'crm' && (
        <div>
          <h2 className="text-xl font-medium mb-4">Customer Relationship</h2>
          <div className="stripe-dashboard-grid">
            {filteredCrmStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View details <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Inventory View Stats */}
      {currentView === 'inventory' && (
        <div>
          <h2 className="text-xl font-medium mb-4">Inventory Overview</h2>
          <div className="stripe-dashboard-grid">
            {filteredInventoryStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View details <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Manufacturing View Stats */}
      {currentView === 'manufacturing' && (
        <div>
          <h2 className="text-xl font-medium mb-4">Manufacturing Operations</h2>
          <div className="stripe-dashboard-grid">
            {filteredManufacturingStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View details <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Procurement View Stats */}
      {currentView === 'procurement' && (
        <div>
          <h2 className="text-xl font-medium mb-4">Procurement Management</h2>
          <div className="stripe-dashboard-grid">
            {filteredProcurementStats.map((stat) => (
              <Card key={stat.name} className="overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                  <div className={cn("p-2 rounded-full", stat.color)}>
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto text-xs text-muted-foreground" asChild>
                    <a href={stat.href} className="flex items-center gap-1">
                      View details <ChevronRight className="w-3 h-3" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Financial overview - only show for dashboard and accounting views and if user has Accounting permission */}
      {(currentView === 'dashboard' || currentView === 'accounting') && canView('Accounting') && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-3">
          <Card className="col-span-1 md:col-span-2 stripe-gradient-bg">
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
              <CardDescription>
                Revenue collected over time
              </CardDescription>
              <div className="flex items-center gap-2 mt-2">
                <Tabs defaultValue="monthly">
                  <TabsList className="grid w-full max-w-[260px] grid-cols-2">
                    <TabsTrigger 
                      value="monthly" 
                      onClick={() => setSelectedTimeframe('monthly')}
                      className={selectedTimeframe === 'monthly' ? 'bg-primary text-primary-foreground' : ''}
                    >
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger 
                      value="yearly" 
                      onClick={() => setSelectedTimeframe('yearly')}
                      className={selectedTimeframe === 'yearly' ? 'bg-primary text-primary-foreground' : ''}
                    >
                      Yearly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent className="px-2">
              {loadingStates.monthlyReceipts ? (
                <div className="h-[300px] w-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={receiptsData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#f0f0f0' }}
                    />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value, currency)}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value as number, currency)}
                      contentStyle={{ borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}
                    />
                    <Bar 
                      dataKey="amount" 
                      name="Revenue" 
                      radius={[4, 4, 0, 0]}
                      fill="hsl(var(--chart-1))" 
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-6">
            {financialStats.map((stat, i) => (
              <Card key={i} className="flex-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-medium">{stat.name}</CardTitle>
                    <CardDescription>{stat.description}</CardDescription>
                  </div>
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-muted">
                    {stat.icon}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value}
                  </div>
                  <p className={cn(
                    "text-xs mt-2 flex items-center",
                    stat.trendUp ? "text-green-500" : "text-muted-foreground"
                  )}>
                    {stat.trendUp && <ArrowUpRight className="mr-1 w-3 h-3" />}
                    {stat.trend}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Sales pipeline visualization - only for sales view */}
      {currentView === 'sales' && (
        <Card className="col-span-1 md:col-span-2 stripe-gradient-bg overflow-hidden">
          <CardHeader>
            <CardTitle>Sales Pipeline</CardTitle>
            <CardDescription>
              Deals by stage and value
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2">
            <div className="h-[300px] w-full flex flex-col justify-center items-stretch">
              {/* Mock pipeline visualization */}
              <div className="flex items-stretch h-36 gap-1 mb-4">
                <div className="bg-blue-100 rounded-md flex-1 flex flex-col justify-between p-3">
                  <span className="text-xs font-medium text-blue-700">Prospecting</span>
                  <span className="text-lg font-bold text-blue-800">$15,000</span>
                  <span className="text-xs text-blue-700">5 deals</span>
                </div>
                <div className="bg-indigo-100 rounded-md flex-1 flex flex-col justify-between p-3">
                  <span className="text-xs font-medium text-indigo-700">Qualification</span>
                  <span className="text-lg font-bold text-indigo-800">$12,000</span>
                  <span className="text-xs text-indigo-700">3 deals</span>
                </div>
                <div className="bg-purple-100 rounded-md flex-1 flex flex-col justify-between p-3">
                  <span className="text-xs font-medium text-purple-700">Proposal</span>
                  <span className="text-lg font-bold text-purple-800">$18,000</span>
                  <span className="text-xs text-purple-700">2 deals</span>
                </div>
                <div className="bg-pink-100 rounded-md flex-1 flex flex-col justify-between p-3">
                  <span className="text-xs font-medium text-pink-700">Negotiation</span>
                  <span className="text-lg font-bold text-pink-800">$7,000</span>
                  <span className="text-xs text-pink-700">2 deals</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="self-end">
                View Full Pipeline
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inventory overview visualization - only for inventory view */}
      {currentView === 'inventory' && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          <Card className="stripe-gradient-bg overflow-hidden">
            <CardHeader>
              <CardTitle>Stock Levels by Category</CardTitle>
              <CardDescription>
                Current inventory distribution
              </CardDescription>
            </CardHeader>
            <CardContent className="px-2">
              <div className="h-[250px] w-full flex flex-col justify-center items-stretch">
                {/* Mock inventory visualization */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Electronics</span>
                    <span className="text-sm text-muted-foreground">45 items</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Furniture</span>
                    <span className="text-sm text-muted-foreground">32 items</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Office Supplies</span>
                    <span className="text-sm text-muted-foreground">89 items</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="self-end mt-4">
                  View All Categories
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Recent Stock Movements</CardTitle>
              <CardDescription>
                Latest inventory transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Truck className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Stock In</p>
                      <p className="text-xs text-muted-foreground">Laptop - Dell XPS</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-green-600">+5</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                      <Package className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Stock Out</p>
                      <p className="text-xs text-muted-foreground">Office Chair</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-red-600">-2</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Truck className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Transfer</p>
                      <p className="text-xs text-muted-foreground">Warehouse A → B</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-blue-600">10</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4">
                View All Movements
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Manufacturing overview visualization - only for manufacturing view */}
      {currentView === 'manufacturing' && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          <Card className="stripe-gradient-bg overflow-hidden">
            <CardHeader>
              <CardTitle>Production Schedule</CardTitle>
              <CardDescription>
                Upcoming production jobs
              </CardDescription>
            </CardHeader>
            <CardContent className="px-2">
              <div className="h-[250px] w-full flex flex-col justify-center">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Product A - Batch #001</p>
                      <p className="text-xs text-muted-foreground">Due: Tomorrow</p>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">In Progress</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Product B - Batch #002</p>
                      <p className="text-xs text-muted-foreground">Due: Next Week</p>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Scheduled</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Product C - Batch #003</p>
                      <p className="text-xs text-muted-foreground">Due: Next Month</p>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Planned</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="self-end mt-4">
                  View Full Schedule
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Quality Control</CardTitle>
              <CardDescription>
                Recent quality checks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <FileCheck className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Batch #001</p>
                      <p className="text-xs text-muted-foreground">Product A</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Passed</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Batch #002</p>
                      <p className="text-xs text-muted-foreground">Product B</p>
                    </div>
                  </div>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Review</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <ClipboardList className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Batch #003</p>
                      <p className="text-xs text-muted-foreground">Product C</p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Testing</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4">
                View Quality Reports
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Procurement overview visualization - only for procurement view */}
      {currentView === 'procurement' && (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          <Card className="stripe-gradient-bg overflow-hidden">
            <CardHeader>
              <CardTitle>Purchase Order Status</CardTitle>
              <CardDescription>
                Current PO distribution
              </CardDescription>
            </CardHeader>
            <CardContent className="px-2">
              <div className="h-[250px] w-full flex flex-col justify-center">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">8</div>
                    <div className="text-xs text-muted-foreground">Draft</div>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">12</div>
                    <div className="text-xs text-muted-foreground">Sent</div>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">15</div>
                    <div className="text-xs text-muted-foreground">Received</div>
                  </div>
                  <div className="bg-white/50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">3</div>
                    <div className="text-xs text-muted-foreground">Partial</div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="self-end">
                  View All Orders
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Recent Approvals</CardTitle>
              <CardDescription>
                Latest procurement approvals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <FileCheck className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">PO-2024-001</p>
                      <p className="text-xs text-muted-foreground">Office Supplies</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Approved</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <ClipboardList className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">PO-2024-002</p>
                      <p className="text-xs text-muted-foreground">IT Equipment</p>
                    </div>
                  </div>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Pending</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <ShoppingCart className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">PO-2024-003</p>
                      <p className="text-xs text-muted-foreground">Raw Materials</p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Review</span>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-4">
                View All Approvals
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CRM recent activity - only for CRM view */}
      {currentView === 'crm' && (
        <Card className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>Recent Customer Activity</CardTitle>
              <CardDescription>Latest interactions with your customers</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href="/crm/activities/new">
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Activity
              </a>
            </Button>
          </CardHeader>
          <CardContent>
            {loadingStates.crmActivities ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : crmActivities.length > 0 ? (
              <div className="space-y-4">
                {crmActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-border/50 last:border-0">
                    <div className={cn("h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0", getActivityColorClass(activity.activityType))}>
                      {getActivityIcon(activity.activityType)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{activity.title}</p>
                      <p className="text-xs text-muted-foreground">{activity.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatActivityDate(activity.date)}</p>
                      {activity.customerName && (
                        <a href={`/customer/${activity.publicId}`} className="text-xs text-primary hover:underline">
                          Customer: {activity.customerName}
                        </a>
                      )}
                      {activity.assignedToName && (
                        <a href={`/customer/${activity.assignedToPublicId}`} className="text-xs text-primary hover:underline">
                          Assigned To: {activity.assignedToName}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p>No recent activities found</p>
                <Button variant="outline" size="sm" className="mt-2" asChild>
                  <a href="/crm/activities/new">Create your first activity</a>
                </Button>
              </div>
            )}
            
            <Button variant="outline" size="sm" className="w-full mt-4" asChild>
              <a href="/crm/activities">View All Activities</a>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recent activity section - always show but with view-specific content */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Recent invoices - only for dashboard and accounting views */}
        {(currentView === 'dashboard' || currentView === 'accounting') && canView('Invoices') && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Recent Invoices</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <a href="/invoices">View all</a>
              </Button>
            </CardHeader>
            <CardContent>
              {loadingStates.invoices ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (analytics.recentInvoices && analytics.recentInvoices.length > 0) ? (
                <div className="space-y-4">
                  {analytics.recentInvoices.slice(0, 5).map((invoice: any, index: number) => (
                    <div 
                      key={invoice.id} 
                      className={cn(
                        "flex items-center justify-between py-3",
                        index !== (analytics.recentInvoices?.length ?? 0) - 1 && "border-b border-border/50"
                      )}
                    >
                      <div className="space-y-1">
                        <p className="text-sm font-medium leading-none"><a href={`/customer/${invoice.customer?.publicId}`} className="hover:underline text-primary">{invoice.customer?.companyName || 'Unknown Customer'}</a></p>
                        <p className="text-xs text-muted-foreground">{invoice.invoiceNumber || `INV-${invoice.id}`}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                          invoice.status === 'paid' ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300" :
                          invoice.status === 'pending' ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300" :
                          "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                        )}>
                          {invoice.status?.replace('_', ' ')}
                        </span>
                        <span className="text-sm font-medium">
                          {formatCurrency(invoice.amount, currency)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No recent invoices found
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recent quotes - only for dashboard and sales views */}
        {(currentView === 'dashboard' || currentView === 'sales') && canView('Quotations') && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Recent Quotations</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <a href="/quotations">View all</a>
              </Button>
            </CardHeader>
            <CardContent>
              {loadingStates.quotations ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (analytics.recentQuotations && analytics.recentQuotations.length > 0) ? (
                <div className="space-y-4">
                  {analytics.recentQuotations.slice(0, 5).map((quotation: any, index: number) => (
                    <div 
                      key={quotation.id} 
                      className={cn(
                        "flex items-center justify-between py-3",
                        index !== (analytics.recentQuotations?.length ?? 0) - 1 && "border-b border-border/50"
                      )}
                    >
                      <div className="space-y-1">
                      <p className="text-sm font-medium leading-none"> <a href={`/customer/${quotation.customer?.publicId}`} className="hover:underline text-primary">{quotation.customer?.companyName || 'Unknown Customer'}</a></p>
                        <p className="text-xs text-muted-foreground">{quotation.quotationNumber || `QUO-${quotation.id}`}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                          quotation.status === 'accepted' ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300" :
                          quotation.status === 'pending' ? "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300" :
                          "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                        )}>
                          {quotation.status}
                        </span>
                        <span className="text-sm font-medium">
                          {formatCurrency(quotation.amount, currency)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No recent quotations found
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}